import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRightIcon, BellIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { getNotificationsRequest, markAsReadUnreadRequest, NotificationsLoadingKeys, markAllAsReadRequest } from '../../slices/managementSlices/notificationsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { arrayHasItems } from '../../helpers/common';
import { LoaderSpinner } from '../LoaderSpinner';
import { useLocation } from 'react-router-dom';
import { ROUTES } from '../../consts/general';
import { useClickOutsideBlock } from '../../helpers/useClickOutside';
import { ConfrimModal } from '../ConfrimModal';

export const NotificationCenter = () => {
  const [isListOpen, setIsListOpen] = useState(false);
  const [openIds, setOpenIds] = useState(new Set());
  const [openMarkAllAsReadModal, setOpenMarkAllAsReadModal] = useState(false);
  const containerRef = useRef(null);
  const { currentUser } = useSelector((state) => state.auth);
  const { 
    sidebarNotifications, 
    loadingKeys: notificationsLoadingKeys, 
    loading: notificationsLoading 
  } = useSelector((state) => state.notifications);
  const notifications = sidebarNotifications?.list || [];
  const unreadCount = sidebarNotifications?.unreadCount || 0;
  const dispatch = useDispatch();
  const currentUserId = currentUser?.id;
  const location = useLocation();

  useEffect(() => {
    if (currentUserId) {
      dispatch(getNotificationsRequest({ userId: currentUserId, all: false }));
    }
  }, [currentUserId, location.pathname]);

  const toggleRead = async (notificationId, readed) => {
    await dispatch(markAsReadUnreadRequest({ userId: currentUserId, notificationId, readed })).unwrap();
    await dispatch(getNotificationsRequest({ userId: currentUserId, all: false })).unwrap();
    if (location.pathname === ROUTES.NOTIFICATIONS) {
      await dispatch(getNotificationsRequest({ userId: currentUserId, all: true })).unwrap();
    }
  };

  const handleNotificationClick = (notification) => {
    setOpenIds((prev) => {
      const copy = new Set(prev);
      if (copy.has(notification.id)) copy.delete(notification.id);
      else copy.add(notification.id);
      return copy;
    });
  };

  const getCategoryPill = (category) => {
    if (category === 'INVENTORY') return { label: 'Inventory', cls: 'bg-indigo-50 text-indigo-700' };
    if (category === 'UTILITIES') return { label: 'Utilities', cls: 'bg-purple-50 text-purple-700' };
    if (category === 'EMAILS') return { label: 'Emails', cls: 'bg-green-50 text-green-700' };
    if (category === 'RENEWALS') return { label: 'Renewals', cls: 'bg-amber-50 text-amber-700' };
    return { label: category, cls: 'bg-gray-100 text-gray-700' };
  };

  const getNotificationsIsLoading = notificationsLoading && notificationsLoadingKeys.includes(NotificationsLoadingKeys.getNotifications);
  const markAsReadUnreadIsLoading = notificationsLoading && notificationsLoadingKeys.includes(NotificationsLoadingKeys.markAsReadUnread);
  const isMarkAllAsReadLoading = notificationsLoading && notificationsLoadingKeys.includes(NotificationsLoadingKeys.markAllAsRead);
  const isLoading = getNotificationsIsLoading || markAsReadUnreadIsLoading;

  // Close on click outside using shared hook, but not while modal is open
  useClickOutsideBlock(containerRef, () => {
    if (!openMarkAllAsReadModal) {
      setIsListOpen(false);
    }
  });

  return (
    <>
      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={() => setIsListOpen((open) => !open)}
          className="relative bg-white inline-flex items-center justify-center rounded-full p-2 hover:bg-gray-100 focus:outline-none"
        >
          <BellIcon className="h-6 w-6 text-gray-700" />
          {unreadCount > 0 && (
            <span key={unreadCount} className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white w-[22px] h-[22px]">
              {getNotificationsIsLoading ? <LoaderSpinner width="10" height="10" border="2" colorSpinner="white" /> : unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {isListOpen && (
          <div className="absolute bottom-0 left-full ml-[15px] w-[420px] rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-30">
            <div className="flex flex-col justify-between gap-2 px-4 py-3 border-b border-gray-100">
              <div className='flex flex-row items-center justify-between gap-2'>
                <p className="text-sm font-medium text-gray-900">Notifications</p>
                <p className="text-xs text-gray-500">
                  {unreadCount} unread • showing latest 10
                </p>
              </div>
              <div className='flex flex-row items-center gap-2 justify-end'>
                <div>
                  <button
                    type="button"
                    onClick={() => setOpenMarkAllAsReadModal(true)}
                    className="text-xs text-white px-3 py-1.5 rounded-md bg-primary hover:bg-transparent border border-primary hover:text-primary transition-colors duration-200 flex flex-row items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isMarkAllAsReadLoading || unreadCount === 0}
                  >
                    {isMarkAllAsReadLoading ? <LoaderSpinner width="15" height="15" border="2" colorSpinner="white" /> : 'Mark all as Read'}
                  </button>
                </div>
                <div>
                  <a href={ROUTES.NOTIFICATIONS} className="text-xs group text-primary px-3 py-1.5 rounded-md bg-transparent border border-primary hover:bg-primary hover:text-white transition-colors duration-200 flex flex-row items-center gap-0.5">
                    Show All
                    <ArrowRightIcon className="h-4 w-4 text-primary group-hover:text-white" />
                  </a>
                </div>
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {!arrayHasItems(notifications) ? (
                <div className="px-4 py-6 text-center text-sm text-gray-500">
                  You have no notifications yet.
                </div>
              ) : (
                notifications.map((notification) => {
                  const pill = getCategoryPill(notification.category);
                  const isOpen = openIds.has(notification.id);
                  return (
                    <div key={notification.id} className="border-b last:border-b-0 border-gray-100">
                      <button
                        type="button"
                        onClick={() => handleNotificationClick(notification)}
                        className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 ${
                          notification.readed ? 'bg-white' : 'bg-blue-50'
                        }`}
                      >
                        <div className="mt-0.5">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${pill.cls}`}>
                            {pill.label}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p title={notification.title} className={`text-sm font-medium truncate ${notification.readed ? 'text-gray-800' : 'text-gray-900'}`}>
                            {notification.title}
                          </p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            {notification.created_at}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const next = !notification.readed;
                            toggleRead(notification.notification_id, String(next));
                          }}
                          className="ml-2 inline-flex items-center justify-center rounded-full p-1 hover:bg-gray-100"
                        >
                          {notification.readed ? (
                            <CheckCircleIcon className="h-4 w-4 text-gray-400" />
                          ) : (
                            <span className="h-2 w-2 rounded-full bg-blue-500" />
                          )}
                        </button>
                      </button>
                      {isOpen && notification.details && (
                        <div className="px-4 py-2">
                          <p className="text-xs text-gray-700 whitespace-pre-line">
                            {notification.details}
                          </p>
                          {typeof notification.url === 'string' && notification.url.startsWith('/') && (
                            <a href={notification.url} className="text-xs text-primary underline">
                              Open in Eadin
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      <ConfrimModal
        isOpen={openMarkAllAsReadModal}
        title={"Are you sure you want to mark all notifications as read?"}
        confirmButtonTitle={"Yes"}
        isLoading={isMarkAllAsReadLoading}
        submitClick={async () => {
          setOpenMarkAllAsReadModal(false);
          await dispatch(
            markAllAsReadRequest({
              userId: currentUserId,
            })
          ).unwrap();
          await dispatch(getNotificationsRequest({ userId: currentUserId, all: false })).unwrap();
          if (location.pathname === ROUTES.NOTIFICATIONS) {
            await dispatch(getNotificationsRequest({ userId: currentUserId, all: true })).unwrap();
          }
        }}
        cancelClick={() => {
          setOpenMarkAllAsReadModal(false);
        }}
      />
    </>
  );
};

